import React, { useState } from "react";
import { Sparkles, Check, X, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { LIFETIME_PRICE_INR } from "../lib/tier";
import { toast } from "sonner";

const PAID_FEATURES = [
  "All 15 scales — pentatonic, blues, all 7 modes, whole-tone, diminished, chromatic",
  "All 24 chord qualities — 7ths, 9ths, 11ths, 13ths, sus, add9, 6ths, half-dim",
  "Full Circle of Fifths + progression builder",
  "No “Blueprint of Piano Calling” watermark on PDFs",
  "One-time payment — lifetime access, no subscription",
];

// Auto-submits an HTML form to PayU with the payload returned by the backend.
function submitToPayU(payload) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = payload.payment_url;
  form.style.display = "none";
  const fields = {
    key: payload.key,
    txnid: payload.txnid,
    amount: payload.amount,
    productinfo: payload.productinfo,
    firstname: payload.firstname,
    email: payload.email,
    phone: payload.phone,
    surl: payload.surl,
    furl: payload.furl,
    hash: payload.hash,
    udf1: payload.udf1 || "",
  };
  for (const [k, v] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = v;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

export default function Paywall() {
  const { user, paywallOpen, closePaywall, login, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const handleUnlock = async () => {
    if (!user) {
      login();
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/payu/create-order", {
        origin: window.location.origin,
      });
      submitToPayU(res.data);
    } catch (e) {
      const msg =
        e?.response?.data?.detail || "Could not start payment. Please try again.";
      toast.error(msg);
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!user) {
      login();
      return;
    }
    setRestoring(true);
    try {
      const res = await api.post("/payu/restore-purchase");
      if (res.data?.ok) {
        toast.success("Purchase restored — you're unlocked!");
        await refreshUser();
        closePaywall();
      } else {
        toast.info(
          res.data?.detail || "No successful payment found on PayU for this account"
        );
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "Couldn't verify with PayU right now. Please try again in a minute.";
      toast.error(msg);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Dialog open={paywallOpen} onOpenChange={(o) => (o ? null : closePaywall())}>
      <DialogContent
        className="rounded-2xl border-2 border-gray-900 max-w-lg p-0 overflow-hidden"
        data-testid="paywall-modal"
      >
        <DialogHeader className="p-6 pb-4 border-b-2 border-gray-900 bg-[#F9FAFB]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#673A80] text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle
                className="text-xl font-semibold tracking-tight"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Unlock KEYVISION — Lifetime
              </DialogTitle>
              <p className="text-xs text-gray-500 tracking-widest uppercase mt-0.5">
                One payment. Yours forever.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 flex flex-col gap-5">
          <div className="flex items-baseline gap-2">
            <span
              className="text-5xl font-semibold tracking-tight text-gray-900"
              style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="paywall-price"
            >
              ₹{LIFETIME_PRICE_INR}
            </span>
            <span className="text-sm text-gray-500 line-through">₹1499</span>
            <span className="text-xs font-bold uppercase tracking-widest text-[#673A80] ml-1">
              Launch price
            </span>
          </div>

          <ul className="flex flex-col gap-3">
            {PAID_FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-800">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[#673A80]/10 text-[#673A80] flex-shrink-0 flex items-center justify-center">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-2 pt-2">
            <Button
              data-testid="paywall-unlock-button"
              onClick={handleUnlock}
              disabled={busy}
              className="rounded-xl bg-[#673A80] hover:bg-[#552d6b] text-white h-12 font-semibold text-base"
            >
              {busy ? (
                "Opening PayU…"
              ) : user ? (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Pay ₹{LIFETIME_PRICE_INR} & unlock
                </>
              ) : (
                "Sign in to unlock"
              )}
            </Button>
            {user && (
              <button
                type="button"
                data-testid="paywall-restore-button"
                onClick={handleRestore}
                disabled={restoring}
                className="text-xs text-gray-600 hover:text-[#673A80] underline underline-offset-2 mt-1 self-center disabled:opacity-60"
              >
                {restoring
                  ? "Checking with PayU…"
                  : "Already paid? Restore purchase"}
              </button>
            )}
            <button
              type="button"
              data-testid="paywall-close-button"
              onClick={closePaywall}
              className="text-xs text-gray-500 hover:text-gray-900 mt-1 self-center flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Not now
            </button>
          </div>

          <p className="text-[11px] text-gray-400 text-center leading-relaxed">
            Payments are processed securely by PayU. You will be redirected to
            PayU&apos;s hosted checkout. On success your account is upgraded to
            Lifetime access instantly.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
